import { Stack } from '@mui/material';
import * as refine from '@refinedev/core';
import { Create, Edit, useAutocomplete } from '@refinedev/mui';
import { useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { uuidv7 } from 'uuidv7';
import type { z } from 'zod';
import { BaseField } from '../../../components/fields/BaseField.tsx';
import { ListField } from '../../../components/fields/ListField.tsx';
import { RichTextField3 } from '../../../components/fields/RichTextField3.tsx';
import type { Sector, Topic } from '../../../types/index.ts';
import { Milestone, Timeline, TimelineId } from '../../../types/index.ts';
import { MilestoneListField } from './MilestoneListField.tsx';

const COMPONENT_MAP = { create: Create, edit: Edit } as const;

const defaultMilestoneList = [
  { type: 'council', name: '制度内容の検討', documents: [] },
  { type: 'diet', name: '国会審議', documents: [] },
  { type: 'conclusion', name: '公布・策定', documents: [] },
  { type: 'preparation', name: '施行に向けた準備', documents: [] },
  { type: 'enforcement', name: '施行', documents: [] },
  { type: 'post-enforcement', name: '施行後の動き', documents: [] },
] satisfies Array<Partial<Milestone>>;

export const TimelineForm = ({ action }: { action: 'create' | 'edit' }) => {
  const { query, formLoading, onFinish } = refine.useForm<Timeline>({
    resource: 'contents/timelines',
    action,
  });

  const data = useMemo(
    () => (query?.data?.data ? Timeline.parse(query?.data?.data) : undefined),
    [query?.data?.data],
  );

  const methods = useForm<Timeline>({
    values: data,
    defaultValues: {
      sectors: [],
      topics: [],
      milestones: defaultMilestoneList.map((milestone) =>
        Milestone.parse({
          ...milestone,
          id: uuidv7(),
          description: null,
        } satisfies z.input<typeof Milestone>),
      ),
    },
  });

  const { autocompleteProps: topicAutocompleteProps } = useAutocomplete<Topic>({
    resource: 'contents/topics',
    filters: [],
    sort: [
      { field: 'category', order: 'asc' },
      { field: 'name', order: 'asc' },
    ],
    onSearch: (value) => [{ field: 'category+name', operator: 'contains', value }],
  });

  const { autocompleteProps: sectorAutocompleteProps } = useAutocomplete<Sector>({
    resource: 'contents/sectors',
    filters: [],
    sort: [
      { field: 'category', order: 'asc' },
      { field: 'name', order: 'asc' },
    ],
    onSearch: (value) => [{ field: 'category+name', operator: 'contains', value }],
  });

  const Component = COMPONENT_MAP[action];

  return (
    <Component
      canDelete
      isLoading={formLoading}
      saveButtonProps={{
        disabled: methods.formState.isSubmitting || formLoading,
        onClick: methods.handleSubmit((value) => {
          value.id ??= TimelineId.parse(uuidv7());

          if (value.description?.trim() === '') {
            value.description = null;
          }

          return onFinish(value);
        }),
      }}
    >
      <FormProvider {...methods}>
        <Stack spacing={2}>
          <BaseField
            name="name"
            rules={{
              required: '必須',
            }}
            label="タイトル"
            fullWidth
          />
          <RichTextField3
            name="description"
            rules={{
              required: '必須',
            }}
            label="概要"
            fullWidth
            height={400}
          />
          <ListField
            {...sectorAutocompleteProps}
            name="sectors"
            label="分野"
            getOptionKey={(item) => item.id}
            getOptionLabel={(item) => `${item.category.name}／${item.name}`}
          />
          <ListField
            {...topicAutocompleteProps}
            name="topics"
            label="トピック"
            getOptionKey={(item) => item.id}
            getOptionLabel={(item) => `${item.category.name}／${item.name}`}
          />
          <MilestoneListField />
        </Stack>
      </FormProvider>
    </Component>
  );
};
